import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import * as authSelectors from "redux/auth/selectors";
import { api } from "utils/hostname";
import url from "../../../../../url";
import type { FortuneWheelAvailabilityResponse } from "../type/FortuneWheel";
import { useFortuneWheelStore } from "../store/fortuneWheel";

type Availability = {
  isAvailable: boolean;
  nextAvailableAt: string;
};

export const useWheelAvailability = () => {
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);

  const query = useQuery<FortuneWheelAvailabilityResponse, Error, Availability>(
    {
      queryKey: ["fortuneWheel", "available"],
      queryFn: async () => {
        const res = await api(true, true).get<FortuneWheelAvailabilityResponse>(
          url.config.api.fortuneWheel.availability,
        );
        const payload = res?.data;
        if (!payload || typeof payload !== "object") {
          throw new Error("Invalid fortune wheel availability payload");
        }
        return payload;
      },
      select: (data) => ({
        isAvailable: Boolean(data.isAvailable),
        nextAvailableAt: data.nextAvailableAt ?? "",
      }),
      enabled: !!isAuthenticated,
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      retry: isAuthenticated ? 2 : 0,
    },
  );

  useEffect(() => {
    const state = useFortuneWheelStore.getState();

    if (!isAuthenticated) {
      if (state.isAvailable !== false) {
        state.setAvailable(false);
      }
      if (state.nextAvailableAt !== "") {
        state.setNextAvailable("");
      }
      if (state.error) {
        state.setError("");
      }
      return;
    }

    const { data } = query;
    if (!data) {
      return;
    }

    if (state.isAvailable !== data.isAvailable) {
      state.setAvailable(data.isAvailable);
    }
    if ((state.nextAvailableAt ?? "") !== data.nextAvailableAt) {
      state.setNextAvailable(data.nextAvailableAt);
    }
    if (state.error) {
      state.setError("");
    }
  }, [isAuthenticated, query, query.data]);

  useEffect(() => {
    if (!isAuthenticated || !query.isError) {
      return;
    }

    const state = useFortuneWheelStore.getState();
    if (state.isAvailable !== false) {
      state.setAvailable(false);
    }
    if (state.nextAvailableAt !== "") {
      state.setNextAvailable("");
    }
    state.setError(query.error?.message || "Failed to fetch availability");
  }, [isAuthenticated, query.isError, query.error]);

  return query;
};
